/**
 * Shared predicates for AP invoice line items used on both the frontend and
 * the backend. Kept here (rather than duplicated per-side) so that "what
 * counts as a real money row" stays consistent across UI display, server-side
 * aggregations (e.g. line-item match summaries), reconciliation matching,
 * and the AI fallback agent.
 */

/**
 * Minimal shape this module needs. Accepts both backend Sequelize DECIMAL
 * strings and frontend prop shapes (`string | number | null | undefined`).
 */
export interface InvoiceLineItemAmounts {
  quantity?: string | number | null;
  unitPrice?: string | number | null;
  totalPrice?: string | number | null;
}

/**
 * AP extractors regularly emit non-money rows (seal numbers, container refs,
 * notes, subtotal/section labels) with `quantity = 0` AND `unitPrice = 0`
 * AND `totalPrice = 0`. Those rows can never match a real PO/SO/TO line, so:
 *   - matching should skip them (otherwise the Hungarian algorithm reports
 *     "more invoice lines than candidates" and drags overall confidence to
 *     0 even when every monetary line matched exactly),
 *   - the UI should hide them from the default line-items view (they're
 *     OCR artifacts, not invoice content),
 *   - the "matched / total" badge should not count them.
 *
 * The filter is intentionally conservative on two axes:
 *
 * 1. `totalPrice` is checked in addition to qty + unitPrice so we don't
 *    accidentally drop real money rows the extractor couldn't decompose:
 *      - lump-sum freight / fuel surcharges: `qty=0, unitPrice=0, totalPrice=250`,
 *      - credits / adjustments:              `qty=0, unitPrice=0, totalPrice=-100`,
 *      - delivery/freight with unit pricing: `qty>0, unitPrice=0, totalPrice=…`,
 *      - free samples:                       `qty=0, unitPrice>0, totalPrice=…`.
 *
 * 2. Only EXPLICITLY numeric zeros count — `null`, `undefined`, blank
 *    strings, and unparseable junk all mean "we don't know", and an unknown
 *    row should pass through rather than be silently dropped. The Sequelize
 *    columns are NOT NULL and the extraction schema requires numbers, so in
 *    production all three are always populated; this strictness mainly
 *    guards against callers (tests, partial DTOs) that omit a field.
 */
export function isMatchableInvoiceLineItem(
  line: InvoiceLineItemAmounts,
): boolean {
  return !(
    isExplicitNumericZero(line.quantity) &&
    isExplicitNumericZero(line.unitPrice) &&
    isExplicitNumericZero(line.totalPrice)
  );
}

/**
 * Inverse of `isMatchableInvoiceLineItem` — useful when collecting hidden
 * rows for "Show N metadata rows" toggles.
 */
export function isInformationalInvoiceLineItem(
  line: InvoiceLineItemAmounts,
): boolean {
  return !isMatchableInvoiceLineItem(line);
}

/**
 * Split invoice line items into matchable (real money rows) vs informational
 * (zero qty AND zero unit price). Generic in `T` so callers keep their full
 * shape on both sides of the partition.
 */
export function partitionMatchableInvoiceLineItems<
  T extends InvoiceLineItemAmounts,
>(lines: readonly T[]): { matchable: T[]; informational: T[] } {
  const matchable: T[] = [];
  const informational: T[] = [];
  for (const line of lines) {
    if (isMatchableInvoiceLineItem(line)) {
      matchable.push(line);
    } else {
      informational.push(line);
    }
  }
  return { matchable, informational };
}

/**
 * True iff `value` is unambiguously the number 0. Rejects nullish, blank
 * strings, and unparseable input — those signal "missing/unknown" rather
 * than an actual zero amount.
 */
function isExplicitNumericZero(
  value: string | number | null | undefined,
): boolean {
  if (value == null) return false;
  if (typeof value === "number") return value === 0;
  const trimmed = value.trim();
  if (trimmed === "") return false;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) && parsed === 0;
}
